import { useState } from "react";
import { Info } from "lucide-react";
import type { AutoPromotionProvider } from "@lucralink/shared";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { AutoPromotionPanel } from "@/components/auto-promotions/AutoPromotionPanel";
import { useAutoPromotionState } from "@/lib/hooks/useAutoPromotions";
import { cn } from "@/lib/utils";

type TabId = AutoPromotionProvider | "amazon";

const TABS: ReadonlyArray<{ id: TabId; label: string }> = [
  { id: "shopee", label: "Shopee" },
  { id: "mercadolivre", label: "Mercado Livre" },
  { id: "amazon", label: "Amazon" },
];

const NOTICES: Readonly<Record<"mercadolivre" | "amazon", { title: string; body: string }>> = {
  mercadolivre: {
    title: "Mercado Livre ainda sem curadoria automática",
    body: "O Mercado Livre não oferece API oficial de busca de ofertas para afiliados, e a sessão capturada pela extensão só gera links. Sem uma fonte oficial com comissão, vendas e nota não dá para escolher produtos com segurança. Continue gerando links pelo Gerar Link e pelo encaminhamento.",
  },
  amazon: {
    title: "Amazon fica de fora da curadoria",
    body: "A Creators API da Amazon não informa comissão, quantidade de vendas nem nota dos produtos, então os filtros da curadoria não funcionariam. Use o Feed de promoções para buscar ofertas da Amazon e gerar o link na hora.",
  },
};

function ProviderNotice({ provider }: { provider: "mercadolivre" | "amazon" }): JSX.Element {
  const notice = NOTICES[provider];
  return (
    <Card>
      <CardContent className="flex gap-3 p-5">
        <Info size={18} className="mt-0.5 shrink-0 text-[hsl(var(--gold))]" aria-hidden="true" />
        <div>
          <h2 className="text-sm font-semibold text-foreground">{notice.title}</h2>
          <p className="mt-1 max-w-2xl text-sm leading-relaxed text-muted-foreground">{notice.body}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function ShopeeTab(): JSX.Element {
  const { data, isLoading, error } = useAutoPromotionState("shopee");
  if (isLoading) {
    return <div className="h-64 animate-pulse rounded-xl border border-border bg-[hsl(var(--surface-raised)/0.4)]" aria-busy="true" />;
  }
  if (error || !data) {
    return (
      <p role="alert" className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
        Não foi possível carregar a configuração. Recarregue a página.
      </p>
    );
  }
  return <AutoPromotionPanel state={data} />;
}

export function AutoPromocoesPage(): JSX.Element {
  const [tab, setTab] = useState<TabId>("shopee");
  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <PageHeader
        title="Promoções automáticas"
        subtitle="O LucraLink busca ofertas nas suas categorias, filtra pelos seus critérios e envia nos seus grupos no ritmo que você escolher."
      />
      <div className="flex gap-1 border-b border-border pb-px" role="tablist" aria-label="Lojas">
        {TABS.map((item) => (
          <button
            key={item.id}
            role="tab"
            aria-selected={tab === item.id}
            onClick={() => setTab(item.id)}
            className={cn(
              "relative px-4 py-2.5 text-sm font-medium transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              tab === item.id ? "text-foreground" : "text-muted-foreground hover:text-foreground",
            )}
          >
            {item.label}
            {tab === item.id && <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-primary" aria-hidden="true" />}
          </button>
        ))}
      </div>
      <div role="tabpanel">{tab === "shopee" ? <ShopeeTab /> : <ProviderNotice provider={tab} />}</div>
    </div>
  );
}
